import React, { createContext, useContext, useEffect, useCallback } from 'react';
import { usePlayerStore } from '../stores/playerStore';
import { useLocalLibraryStore } from '../stores/localLibraryStore';
import { useToast } from '../hooks/useToast';
import { useAudioPlayer } from '../hooks/useAudioPlayer';
import { useHapticEngine } from '../hooks/useHapticsEngine';
import { useAudioContext } from './AudioContextProvider';
import { getShortcutByKey } from '../config/shortcuts';

interface ShortcutsContextType {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
}

const ShortcutsContext = createContext<ShortcutsContextType>({
  enabled: true,
  setEnabled: () => {},
});

export const useShortcuts = () => useContext(ShortcutsContext);

interface ShortcutsProviderProps {
  children: React.ReactNode;
}

export const ShortcutsProvider: React.FC<ShortcutsProviderProps> = ({ children }) => {
  const [enabled, setEnabled] = React.useState(true);
  const { toast } = useToast();
  const {
    isPlaying,
    currentTrack,
    currentTime,
    volume,
    loopMode,
    setIsPlaying,
    setVolume,
    setLoopMode,
    playNext,
    playPrevious,
  } = usePlayerStore();
  const { isContentLiked, toggleLike } = useLocalLibraryStore();
  const { audioContext } = useAudioContext();
  const { settings: hapticsSettings, toggleEnabled: toggleHaptics } = useHapticEngine(audioContext);
  const { seekByPercentage } = useAudioPlayer();

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<Element>) => {
      // Don't trigger shortcuts if disabled or if target is an input/textarea
      if (
        !enabled ||
        (event.target instanceof HTMLElement &&
          (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA'))
      ) {
        return;
      }

      const shortcut = getShortcutByKey(event);
      if (!shortcut) return;

      event.preventDefault();

      switch (shortcut.id) {
        case 'toggle-play':
          setIsPlaying(!isPlaying);
          toast({
            description: `${isPlaying ? 'Paused' : 'Playing'} ${currentTrack?.title || 'track'}`,
            duration: 1000,
          });
          break;

        case 'seek-forward':
          seekByPercentage(Math.min(100, (currentTime + 10) / (currentTrack?.duration || 1) * 100));
          toast({
            description: 'Seeking forward 10 seconds',
            duration: 1000,
          });
          break;

        case 'seek-backward':
          seekByPercentage(Math.max(0, (currentTime - 10) / (currentTrack?.duration || 1) * 100));
          toast({
            description: 'Seeking backward 10 seconds',
            duration: 1000,
          });
          break;

        case 'next-track':
          playNext();
          toast({
            description: 'Playing next track',
            duration: 1000,
          });
          break;

        case 'previous-track':
          playPrevious();
          toast({
            description: 'Playing previous track',
            duration: 1000,
          });
          break;

        case 'volume-up':
          const newVolumeUp = Math.min(1, volume + 0.1);
          setVolume(newVolumeUp);
          toast({
            description: `Volume: ${Math.round(newVolumeUp * 100)}%`,
            duration: 1000,
          });
          break;

        case 'volume-down':
          const newVolumeDown = Math.max(0, volume - 0.1);
          setVolume(newVolumeDown);
          toast({
            description: `Volume: ${Math.round(newVolumeDown * 100)}%`,
            duration: 1000,
          });
          break;

        case 'toggle-loop':
          const nextLoopMode = loopMode === 'off' ? 'one' : loopMode === 'one' ? 'all' : 'off';
          setLoopMode(nextLoopMode);
          toast({
            description: `Loop mode: ${nextLoopMode}`,
            duration: 1000,
          });
          break;

        case 'toggle-haptics':
          toggleHaptics();
          toast({
            description: `Haptics ${hapticsSettings.enabled ? 'disabled' : 'enabled'}`,
            duration: 1000,
          });
          break;

        case 'toggle-like':
          if (currentTrack) {
            toggleLike(currentTrack.id, 'track', currentTrack.source as 'local' | 'spotify');
            toast({
              description: `Track ${isContentLiked(currentTrack.id, 'track') ? 'removed from' : 'added to'} favorites`,
              duration: 1000,
            });
          }
          break;
      }
    },
    [
      enabled,
      isPlaying,
      currentTrack,
      currentTime,
      volume,
      loopMode,
      hapticsSettings.enabled,
      setIsPlaying,
      setVolume,
      setLoopMode,
      playNext,
      playPrevious,
      seekByPercentage,
      toggleHaptics,
      toggleLike,
      isContentLiked,
      toast,
    ]
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      handleKeyPress(e as unknown as React.KeyboardEvent<Element>);
    };
    
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleKeyPress]);

  return (
    <ShortcutsContext.Provider value={{ enabled, setEnabled }}>
      {children}
    </ShortcutsContext.Provider>
  );
};
